import React, { useState, useRef, useEffect } from 'react';
import { BoltIcon, CheckCircleIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
// eslint-disable-next-line no-unused-vars
import { useFeatureAccess } from '../hooks/useFeatureAccess';
// eslint-disable-next-line no-unused-vars
import { FeatureAccessGate } from '../components/FeatureAccessGate';
import DemoManualEntry from '../components/solver/DemoManualEntry copy';
import SEO from '../components/SEO';
import QuickReference from '../components/solver/QuickReference';
import ecosystemImage from '../assets/images/Image 1_Ecosystem.jpg';
import { useEcosystemSolver } from '../hooks/useEcosystemSolver';
import Results from '../components/solver/Results';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Import ecosystem images
import coralEco1Wahoo from '../assets/EcosystemData/Coral Ecosystem 1/Wahoo.png';
import coralEco1Stingray from '../assets/EcosystemData/Coral Ecosystem 1/Short-tail Stingray.png';
import coralEco1Parrotfish from '../assets/EcosystemData/Coral Ecosystem 1/Queen Parrotfish.png';
import coralEco1Angelfish from '../assets/EcosystemData/Coral Ecosystem 1/Queen Angelfish.png';
import coralEco1Turtle from '../assets/EcosystemData/Coral Ecosystem 1/Hawksbill Sea Turtle.png';
import coralEco1Tang from '../assets/EcosystemData/Coral Ecosystem 1/Gem Tang.png';
import coralEco1Rabbitfish from '../assets/EcosystemData/Coral Ecosystem 1/Foxface Rabbitfish.png';
import coralEco1Dolphinfish from '../assets/EcosystemData/Coral Ecosystem 1/Common Dolphinfish.png';
import coralEco1BlueAngelfish from '../assets/EcosystemData/Coral Ecosystem 1/Blue Striped Angelfish.png';
import coralEco1Bicolor from '../assets/EcosystemData/Coral Ecosystem 1/Bicolor Parrot.png';
import coralEco1Widgeon from '../assets/EcosystemData/Coral Ecosystem 1/Widgeon Grass.png';
import coralEco1FireCoral from '../assets/EcosystemData/Coral Ecosystem 1/Fire Coral.png';
import coralEco1EelGrass from '../assets/EcosystemData/Coral Ecosystem 1/Common Eel Grass.png';

// Import Coral Ecosystem 2 screenshots
import coralEco2Screenshot1 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.45.png';
import coralEco2Screenshot2 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.42.png';
import coralEco2Screenshot3 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.38.png';
import coralEco2Screenshot4 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.35.png';
import coralEco2Screenshot5 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.32.png';
import coralEco2Screenshot6 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.29.png';
import coralEco2Screenshot7 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.26.png';
import coralEco2Screenshot8 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.22.png';
import coralEco2Screenshot9 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.19.png';
import coralEco2Screenshot10 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.14.png';
import coralEco2Screenshot11 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.11.png';
import coralEco2Screenshot12 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.08.png';
import coralEco2Screenshot13 from '../assets/EcosystemData/Coral Ecosystem 2/Screenshot 2025-05-06 at 17.50.04.png';

// Import Coral Ecosystem 3 screenshots
import coralEco3Screenshot1 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.27.16.png';
import coralEco3Screenshot2 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.27.13.png';
import coralEco3Screenshot3 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.27.08.png';
import coralEco3Screenshot4 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.27.05.png';
import coralEco3Screenshot5 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.27.02.png';
import coralEco3Screenshot6 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.59.png';
import coralEco3Screenshot7 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.56.png';
import coralEco3Screenshot8 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.52.png';
import coralEco3Screenshot9 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.49.png';
import coralEco3Screenshot10 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.45.png';
import coralEco3Screenshot11 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.42.png';
import coralEco3Screenshot12 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.38.png';
import coralEco3Screenshot13 from '../assets/EcosystemData/Coral Ecosystem 3/Screenshot 2025-05-06 at 17.26.33.png';

// Import Mountain Ecosystem 1 screenshots
import mountainEco1Screenshot1 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.10.00.png';
import mountainEco1Screenshot2 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.57.png';
import mountainEco1Screenshot3 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.54.png';
import mountainEco1Screenshot4 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.51.png';
import mountainEco1Screenshot5 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.48.png';
import mountainEco1Screenshot6 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.44.png';
import mountainEco1Screenshot7 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.39.png';
import mountainEco1Screenshot8 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.36.png';
import mountainEco1Screenshot9 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.33.png';
import mountainEco1Screenshot10 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.30.png';
import mountainEco1Screenshot11 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.26.png';
import mountainEco1Screenshot12 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.23.png';
import mountainEco1Screenshot13 from '../assets/EcosystemData/Mountain Ecosystem 1/Screenshot 2025-05-06 at 18.09.17.png';

// Import Mountain Ecosystem 2 screenshots
import mountainEco2Screenshot1 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.23.png';
import mountainEco2Screenshot2 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.20.png';
import mountainEco2Screenshot3 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.17.png';
import mountainEco2Screenshot4 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.14.png';
import mountainEco2Screenshot5 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.11.png';
import mountainEco2Screenshot6 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.08.png';
import mountainEco2Screenshot7 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.05.png';
import mountainEco2Screenshot8 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.42.02.png';
import mountainEco2Screenshot9 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.41.59.png';
import mountainEco2Screenshot10 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.41.55.png';
import mountainEco2Screenshot11 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.41.52.png';
import mountainEco2Screenshot12 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.41.48.png';
import mountainEco2Screenshot13 from '../assets/EcosystemData/Mountain Ecosystem 2/Screenshot 2025-05-07 at 17.41.44.png';

// Import Mountain Ecosystem 3 screenshots
import mountainEco3Screenshot1 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-07 at 18.13.05.png';
import mountainEco3Screenshot2 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-07 at 18.12.58.png';
import mountainEco3Screenshot3 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.40.png';
import mountainEco3Screenshot4 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.37.png';
import mountainEco3Screenshot5 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.33.png';
import mountainEco3Screenshot6 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.30.png';
import mountainEco3Screenshot7 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.26.png';
import mountainEco3Screenshot8 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.23.png';
import mountainEco3Screenshot9 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.20.png';
import mountainEco3Screenshot10 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.17.png';
import mountainEco3Screenshot11 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.11.png';
import mountainEco3Screenshot12 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.08.png';
import mountainEco3Screenshot13 from '../assets/EcosystemData/MountainEcosystem3/Screenshot 2025-05-06 at 18.44.02.png';

// Create an object to map ecosystem names to their images
const ecosystemImages = {
  'Coral Ecosystem 1': [
    coralEco1Wahoo,
    coralEco1Stingray,
    coralEco1Parrotfish,
    coralEco1Angelfish,
    coralEco1Turtle,
    coralEco1Tang,
    coralEco1Rabbitfish,
    coralEco1Dolphinfish,
    coralEco1BlueAngelfish,
    coralEco1Bicolor,
    coralEco1Widgeon,
    coralEco1FireCoral,
    coralEco1EelGrass
  ],
  'Coral Ecosystem 2': [
    coralEco2Screenshot1,
    coralEco2Screenshot2,
    coralEco2Screenshot3,
    coralEco2Screenshot4,
    coralEco2Screenshot5,
    coralEco2Screenshot6,
    coralEco2Screenshot7,
    coralEco2Screenshot8,
    coralEco2Screenshot9,
    coralEco2Screenshot10,
    coralEco2Screenshot11,
    coralEco2Screenshot12,
    coralEco2Screenshot13
  ],
  'Coral Ecosystem 3': [
    coralEco3Screenshot1,
    coralEco3Screenshot2,
    coralEco3Screenshot3,
    coralEco3Screenshot4,
    coralEco3Screenshot5,
    coralEco3Screenshot6,
    coralEco3Screenshot7,
    coralEco3Screenshot8,
    coralEco3Screenshot9,
    coralEco3Screenshot10,
    coralEco3Screenshot11,
    coralEco3Screenshot12,
    coralEco3Screenshot13
  ],
  'Mountain Ecosystem 1': [
    mountainEco1Screenshot1,
    mountainEco1Screenshot2,
    mountainEco1Screenshot3,
    mountainEco1Screenshot4,
    mountainEco1Screenshot5,
    mountainEco1Screenshot6,
    mountainEco1Screenshot7,
    mountainEco1Screenshot8,
    mountainEco1Screenshot9,
    mountainEco1Screenshot10,
    mountainEco1Screenshot11,
    mountainEco1Screenshot12,
    mountainEco1Screenshot13
  ],
  'Mountain Ecosystem 2': [
    mountainEco2Screenshot1,
    mountainEco2Screenshot2,
    mountainEco2Screenshot3,
    mountainEco2Screenshot4,
    mountainEco2Screenshot5,
    mountainEco2Screenshot6,
    mountainEco2Screenshot7,
    mountainEco2Screenshot8,
    mountainEco2Screenshot9,
    mountainEco2Screenshot10,
    mountainEco2Screenshot11,
    mountainEco2Screenshot12,
    mountainEco2Screenshot13
  ],
  'Mountain Ecosystem 3': [
    mountainEco3Screenshot1,
    mountainEco3Screenshot2,
    mountainEco3Screenshot3,
    mountainEco3Screenshot4,
    mountainEco3Screenshot5,
    mountainEco3Screenshot6,
    mountainEco3Screenshot7,
    mountainEco3Screenshot8,
    mountainEco3Screenshot9,
    mountainEco3Screenshot10,
    mountainEco3Screenshot11,
    mountainEco3Screenshot12,
    mountainEco3Screenshot13
  ]
};

// Add custom styles for enhanced interactions
const styles = `
  .solver-card {
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.92) 100%);
    border: 1px solid rgba(59, 130, 246, 0.1);
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1),
                inset 0 8px 16px rgba(0, 0, 0, 0.1);
    position: relative;
    border-radius: 20px;
    margin: 0 auto;
  }

  @media (min-width: 640px) {
    .solver-card {
      border-radius: 40px;
    }
  }

  .solver-card.ocr-review {
    max-width: 95vw;
  }

  @media (min-width: 768px) {
    .solver-card.ocr-review {
      max-width: 90vw;
    }
  }

  .solver-card:not(.ocr-review) {
    max-width: 900px;
  }

  .solver-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  .method-card {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
    border: 1px solid rgba(59, 130, 246, 0.1);
    overflow: hidden;
    position: relative;
    border-radius: 32px;
  }

  .method-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, #60A5FA, #93C5FD);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .method-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 20px 30px -15px rgba(66, 153, 225, 0.15);
    border-color: rgba(59, 130, 246, 0.2);
  }

  .method-card:hover::before {
    opacity: 1;
  }

  .method-card.active {
    background: linear-gradient(165deg, #F0F9FF 0%, #E1F2FE 100%);
    border-color: rgba(59, 130, 246, 0.2);
  }

  .premium-button {
    position: relative;
    overflow: hidden;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
    border: 1px solid rgba(59, 130, 246, 0.1);
    transition: all 0.3s ease;
    border-radius: 24px;
  }

  .premium-button::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 100%
    );
    transform: rotate(45deg);
    animation: shine 3s infinite;
  }

  .premium-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    border-color: rgba(59, 130, 246, 0.2);
  }

  @keyframes shine {
    0% {
      transform: translateX(-100%) rotate(45deg);
    }
    100% {
      transform: translateX(100%) rotate(45deg);
    }
  }

  .floating-label {
    position: absolute;
    top: -24px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #3B82F6;
    letter-spacing: 0.05em;
  }

  .floating-label::before,
  .floating-label::after {
    content: '';
    height: 1px;
    width: 24px;
    background: linear-gradient(to right, transparent, #3B82F6);
  }

  .floating-label::after {
    background: linear-gradient(to left, transparent, #3B82F6);
  }

  .premium-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #F0F9FF 0%, #E1F2FE 100%);
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    transition: all 0.3s ease;
  }
  
  @media (min-width: 640px) {
    .premium-icon {
      width: 40px;
      height: 40px;
      border-radius: 20px;
      font-size: 20px;
    }
  }

  .premium-button:hover .premium-icon {
    transform: scale(1.1) rotate(-5deg);
  }

  .action-button {
    background: linear-gradient(to right, #60A5FA, #93C5FD);
    color: white;
    padding: 12px 24px;
    border-radius: 24px;
    font-weight: 600;
    transition: all 0.3s ease;
    border: none;
    position: relative;
    overflow: hidden;
  }

  .action-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  .action-button:hover::before {
    transform: translateX(100%);
  }

  .progress-step {
    position: relative;
    transition: all 0.3s ease;
  }

  .progress-step::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.3s ease;
    border-radius: 50%;
    z-index: -1;
  }

  .progress-step:hover::before {
    transform: translate(-50%, -50%) scale(1);
  }

  .upload-zone {
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%);
    border: 2px dashed rgba(59, 130, 246, 0.3);
    transition: all 0.3s ease;
    border-radius: 32px;
  }

  .upload-zone:hover {
    border-color: rgba(59, 130, 246, 0.5);
    background: linear-gradient(165deg, rgba(37, 99, 235, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
  }

  .floating-decoration {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.2));
    filter: blur(20px);
    z-index: 0;
    animation: float 20s infinite ease-in-out;
  }

  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(10px, -10px) rotate(5deg); }
    50% { transform: translate(-5px, 5px) rotate(-5deg); }
    75% { transform: translate(-10px, -5px) rotate(3deg); }
  }

  .results-card {
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    border-radius: 36px;
  }

  .stat-card {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(59, 130, 246, 0.2);
    transition: all 0.3s ease;
    border-radius: 32px;
    padding: 1.5rem;
  }

  .stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
  }

  .premium-card {
    position: relative;
    overflow: hidden;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
    border: 2px solid rgba(59, 130, 246, 0.1);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .premium-card::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 140px;
    height: 140px;
    background: rgba(219, 234, 254, 0.4);
    border-bottom-left-radius: 100%;
    transform: translate(70px, -70px);
    transition: all 0.4s ease;
  }

  .premium-card::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 140px;
    height: 140px;
    background: rgba(219, 234, 254, 0.4);
    border-top-right-radius: 100%;
    transform: translate(-70px, 70px);
    transition: all 0.4s ease;
  }

  .premium-card:hover::before {
    transform: translate(60px, -60px) scale(1.1);
  }

  .premium-card:hover::after {
    transform: translate(-60px, 60px) scale(1.1);
  }

  .animated-label {
    position: absolute;
    top: -28px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
  }

  .animated-label::before,
  .animated-label::after {
    content: '';
    height: 1px;
    width: 24px;
    background: linear-gradient(to right, transparent, #3B82F6);
  }

  .animated-label::after {
    background: linear-gradient(to left, transparent, #3B82F6);
  }

  .animated-text {
    color: #3B82F6;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.8; transform: scale(1.05); }
  }

  .rotate-button {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: linear-gradient(to right, #60A5FA, #93C5FD);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 10px rgba(59, 130, 246, 0.2);
  }
  
  @media (min-width: 640px) {
    .rotate-button {
      width: 44px;
      height: 44px;
      font-size: 20px;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
    }
  }

  .rotate-button:hover {
    transform: scale(1.1) rotate(180deg);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
  }

  .unlock-text {
    background: linear-gradient(135deg, #3B82F6, #60A5FA);
    -webkit-background-clip: text;
    color: transparent;
    font-weight: 600;
    font-size: 16px;
    letter-spacing: 0.02em;
  }

  .title-container {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .premium-title {
    font-size: 24px;
    font-weight: 600;
    background: linear-gradient(135deg, #3B82F6, #60A5FA);
    -webkit-background-clip: text;
    color: transparent;
    position: relative;
    letter-spacing: 0.02em;
  }
  
  @media (min-width: 640px) {
    .premium-title {
      font-size: 28px;
    }
  }

  .title-underline {
    height: 2px;
    width: 100%;
    background: linear-gradient(to right, #3B82F6, transparent);
    margin-top: 4px;
    transform-origin: left;
    animation: expandWidth 0.6s ease-out forwards;
  }

  @keyframes expandWidth {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  .math-symbol {
    position: absolute;
    font-size: 24px;
    color: rgba(59, 130, 246, 0.2);
    pointer-events: none;
    transition: all 0.4s ease;
  }

  .solver-container {
    position: relative;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    border-radius: inherit;
  }

  .solver-container::before {
    content: '';
    position: absolute;
    inset: -1px;
    background: linear-gradient(165deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
    border-radius: inherit;
    z-index: -2;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .solver-container::after {
    content: '';
    position: absolute;
    inset: 0;
    background: inherit;
    border-radius: inherit;
    z-index: -1;
  }

  .solver-container:hover::before {
    opacity: 1;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }

  @keyframes fade {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.8; }
  }

  @keyframes scale {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.5); }
  }

  /* Update the progress steps styles */
  .progress-container {
    position: relative;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 10px;
  }

  .steps-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 40px;
    position: relative;
  }

  .step-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 70px;
  }

  @media (max-width: 480px) {
    .step-container {
      width: 60px;
      gap: 6px;
    }
  }

  .step-number {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    font-weight: 600;
    font-size: 1.25rem;
    transition: all 0.3s ease;
    background: linear-gradient(145deg, #ffffff, #f5f7fa);
    box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1),
                -5px -5px 15px rgba(255, 255, 255, 0.8);
  }

  @media (max-width: 480px) {
    .step-number {
      width: 42px;
      height: 42px;
      font-size: 1rem;
      border-radius: 12px;
    }
  }

  .step-active .step-number {
    background: linear-gradient(145deg, #3B82F6, #60A5FA);
    color: white;
    box-shadow: 0 10px 20px -10px rgba(59, 130, 246, 0.5);
  }

  .step-label {
    font-weight: 500;
    font-size: 0.9rem;
    color: #64748b;
    transition: all 0.3s ease;
    white-space: nowrap;
    text-align: center;
  }

  @media (max-width: 480px) {
    .step-label {
      font-size: 0.75rem;
    }
  }

  .step-active .step-label {
    color: #3B82F6;
    font-weight: 600;
  }

  .progress-track {
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% - 80px);
    height: 3px;
    background: linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
    border-radius: 999px;
  }

  .progress-fill {
    position: absolute;
    height: 100%;
    background: linear-gradient(to right, #3B82F6, #60A5FA);
    border-radius: 999px;
    transition: width 0.5s ease;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const DemoEcosystemSolverPage = () => {
  // eslint-disable-next-line no-unused-vars
  const [inputMethod] = useState('manual');
  const [ecosystemData, setEcosystemData] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const solverRef = useRef(null);
  const [mathSymbols, setMathSymbols] = useState([]);
  const [producersState, setProducersState] = useState([]);
  const [animalsState, setAnimalsState] = useState([]);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [showEcosystemData, setShowEcosystemData] = useState(false);
  const [selectedEcosystem, setSelectedEcosystem] = useState(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Use our hook
  const { solve, result, loading } = useEcosystemSolver();

  // Add mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add ecosystem data sets
  const ecosystemDataSets = [
    {
      name: "Coral Ecosystem 1",
      description: "Demo coral reef ecosystem dataset",
      icon: "🐠",
      stats: "Demo Ecosystem",
      path: "/src/assets/Ecosystem Data/Coral Ecosystem 1"
    }
  ];

  const scrollToSolver = () => {
    solverRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateRandomSymbols = () => {
    const symbols = ['∑', '∏', '∆', '∇', '∫', '≈', '≠', '∞', '±', '⊕', '⊗', '∈', '∉', '∩', '∪'];
    
    // Generate more symbols for better coverage
    const positions = Array.from({ length: 24 }, (_, index) => {
      return {
        symbol: symbols[Math.floor(Math.random() * symbols.length)],
        // Spread across full container width and height
        top: Math.floor(Math.random() * 85) + 5, // Keep 5% padding from edges
        left: Math.floor(Math.random() * 85) + 5, // Keep 5% padding from edges
        scale: Math.random() * 0.5 + 1, // Random size between 1x and 1.5x
        opacity: Math.random() * 0.3 + 0.1, // Random opacity between 0.1 and 0.4
        delay: index * 0.1
      };
    });
    setMathSymbols(positions);
  };

  useEffect(() => {
    generateRandomSymbols();
  }, []);

  // eslint-disable-next-line no-unused-vars
  const getAnimationByType = (type) => {
    const transitions = {
      duration: 3,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut"
    };

    switch (type) {
      case 'float':
        return {
          animate: {
            y: [-10, 10],
            x: [-5, 5],
            rotate: [-5, 5]
          },
          transition: transitions
        };
      case 'spin':
        return {
          animate: {
            rotate: [0, 360]
          },
          transition: transitions
        };
      case 'bounce':
        return {
          animate: {
            y: [0, -20]
          },
          transition: transitions
        };
      case 'fade':
        return {
          animate: {
            opacity: [0.2, 0.8]
          },
          transition: transitions
        };
      case 'scale':
        return {
          animate: {
            scale: [1, 1.5]
          },
          transition: transitions
        };
      default:
        return {
          animate: {
            opacity: [0.2, 0.8]
          },
          transition: transitions
        };
    }
  };

  // eslint-disable-next-line no-unused-vars
  const renderSolverSteps = () => (
    <div className="progress-container">
      <div className="steps-wrapper">
        {[1, 2, 3].map((step) => {
          const tooltipText =
            step === 1
              ? 'Enter your ecosystem data manually'
              : step === 2
              ? 'Edit your data before solving'
              : 'View your ecosystem results here';
          return (
            <motion.div
              key={step}
              className={`step-container ${step <= activeStep ? 'step-active' : ''}`}
              onClick={() => {
                if (step === 1) {
                  setActiveStep(1);
                  setEcosystemData(null);
                } else if (step === 2) {
                  setActiveStep(2);
                }
              }}
              whileHover={{ scale: step === 1 || step === 2 ? 1.05 : 1 }}
              whileTap={{ scale: step === 1 || step === 2 ? 0.95 : 1 }}
              style={{ cursor: step === 1 || step === 2 ? 'pointer' : 'default', position: 'relative' }}
              onMouseEnter={() => setHoveredStep(step)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              {/* Tooltip */}
              {hoveredStep === step && (
                <div style={{
                  position: 'absolute',
                  top: '-30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#111',
                  color: '#fff',
                  padding: '3px 10px',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  whiteSpace: 'nowrap',
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  pointerEvents: 'none',
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                }}>
                  {tooltipText}
                </div>
              )}
              <motion.div
                className="step-number"
                animate={{
                  scale: step === activeStep ? 1.1 : 1,
                }}
              >
                {step}
              </motion.div>
              <span className="step-label">
                {step === 1 ? 'Input Data' : step === 2 ? 'Review' : 'Get Results'}
              </span>
            </motion.div>
          );
        })}
        <div className="progress-track">
          <motion.div
            className="progress-fill"
            initial={{ width: '0%' }}
            animate={{ width: `${((activeStep - 1) / 2) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderMethodSelection = () => (
    <div className={`transition-all duration-300 ${activeStep === 1 ? 'opacity-100 scale-100' : 'opacity-50 scale-95'}`}>
      <div className="grid grid-cols-1 gap-4 mb-8">
        {/* Manual Entry Card */}
        <div className="flex flex-col items-stretch">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setActiveStep(2);
            }}
            className="premium-card p-5 sm:p-8 rounded-xl cursor-pointer relative"
          >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="premium-icon">
                  ✍️
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">Manual Entry</h3>
                  <div className="h-1 w-8 sm:w-12 bg-gradient-to-r from-blue-400 to-transparent rounded"></div>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.9 }}
                className="rotate-button"
              >
                <span>🎯</span>
              </motion.div>
            </div>
            <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
              Input ecosystem data manually with our intuitive interface
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderDataEntry = () => (
    <motion.div
      ref={solverRef}
      className="solver-container solver-card p-6 sm:p-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <DemoManualEntry 
        initialData={ecosystemData}
        onDataChange={(data) => {
          if (data.solved) {
            setEcosystemData(data);
            // Prepare the input for the hook's solve function
            const solverInput = {
              ecosystemName: data.metadata.ecosystemName,
              depthTemperature: {
                minDepth: data.metadata.minDepth,
                maxDepth: data.metadata.maxDepth,
                minTemp: data.metadata.minTemp,
                maxTemp: data.metadata.maxTemp
              },
              producers: data.producers.map((p) => ({
                name: p.Name,
                calories: p['Calories Provided'],
                eatenBy: p['Eaten By']
              })),
              animals: data.animals.map((a) => ({
                name: a.Name,
                caloriesNeeded: a['Calories Needed'],
                caloriesProvided: a['Calories Provided'],
                foodSources: a['Food Sources']
              }))
            };
            solve(solverInput);
            setActiveStep(3);
          }
        }}
        onStateChange={(state) => {
          setProducersState(state.producers);
          setAnimalsState(state.animals);
        }}
        setActiveStep={setActiveStep}
      />
    </motion.div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderSolvingAnimation = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative py-12"
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-48 h-48">
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-blue-200"
            style={{ borderTopColor: '#3B82F6' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-3xl font-bold text-blue-600">{loading ? 'Loading...' : 'Solving...'}</span>
              <div className="text-sm text-gray-500 mt-2">Solving...</div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg"
        >
          <span className="animate-pulse">🧮</span>
          Crunching the numbers...
        </motion.div>
      </div>
    </motion.div>
  );

  // Function to handle ecosystem selection
  const handleEcosystemClick = (ecosystem) => {
    setSelectedEcosystem(ecosystem);
    setShowOptionsModal(true);
  };

  // Function to download ecosystem images as zip
  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const zip = new JSZip();
      const ecosystemFolder = zip.folder(selectedEcosystem.name);

      // Get the images for the selected ecosystem
      const images = ecosystemImages[selectedEcosystem.name] || [];

      // Fetch and add each image to the zip (wait for all to finish)
      await Promise.all(
        images.map(async (image, index) => {
          try {
            const response = await fetch(image);
            if (!response.ok) throw new Error(`Failed to fetch image ${index + 1}`);
            const blob = await response.blob();
            ecosystemFolder.file(`image_${index + 1}.png`, blob);
          } catch (error) {
            // console.error(`Error processing image ${index + 1}:`, error);
          }
        })
      );

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${selectedEcosystem.name.toLowerCase().replace(/\s+/g, '_')}.zip`);
    } catch (error) {
      // console.error('Error creating zip:', error);
      alert('Failed to create zip file. Please try again.');
    } finally {
      setIsLoading(false);
      setShowOptionsModal(false);
    }
  };

  // Function to view ecosystem images
  const handleViewImages = () => {
    try {
      const images = ecosystemImages[selectedEcosystem.name] || [];
      if (images.length === 0) {
        throw new Error('No images found for this ecosystem');
      }
      setViewerImages(images);
      setShowImageViewer(true);
    } catch (error) {
      // console.error('Error loading images:', error);
      alert('Failed to load images. Please try again.');
    } finally {
      setShowOptionsModal(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const renderEcosystemSelection = () => (
    <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
      <div className="bg-blue-50 rounded-lg p-4 mb-4">
        <h4 className="font-medium text-blue-900 text-sm sm:text-base mb-2">Demo Mode</h4>
        <p className="text-xs sm:text-sm text-blue-700">
          This is a demo version that allows you to try the ecosystem solver with our Coral Ecosystem 1 dataset.
          Upgrade to the full version to analyze your own ecosystems!
        </p>
      </div>
      {ecosystemDataSets.map((data, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-blue-50 rounded-lg p-2 sm:p-3 hover:bg-blue-100 transition-colors cursor-pointer group"
          onClick={() => handleEcosystemClick(data)}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="text-lg sm:text-xl group-hover:scale-110 transition-transform">{data.icon}</span>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 text-sm sm:text-base">{data.name}</h4>
              <p className="text-xs sm:text-sm text-blue-700">{data.stats}</p>
            </div>
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              →
            </motion.div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <SEO
        title="Demo Ecosystem Solver | Cognidoor"
        description="Try the McKinsey ecosystem solver demo. Upload or enter creature data and get optimal placement recommendations."
        url="/demo-ecosystem-solver"
        noIndex
      />
      {/* Remove the demo indicator */}
      {/* {renderDemoIndicator()} */}
      
      {/* Rest of the component */}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
        {/* Hero Section with Mountain Background */}
        <div className="relative h-[500px] md:h-[600px] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-gray-800/70 to-gray-700/60">
            <div className="absolute inset-0" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'overlay'
            }} />
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="w-full">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-2xl hero-content"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="inline-block bg-gradient-to-r from-blue-600 to-blue-400 text-white px-4 sm:px-6 py-1 sm:py-2 rounded-full shadow-lg mb-3 sm:mb-4 coming-soon-banner"
                >
                  <span className="font-bold text-sm sm:text-lg tracking-wide coming-soon-text">Solve Like a Pro</span>
                </motion.div>
                <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight text-white">
                  Try for Free: See how our Solver Works!
                </h1>
                <p className="text-base sm:text-xl md:text-2xl text-blue-100 mb-5 sm:mb-10 leading-relaxed">
                  Master ocean or mountain ridge ecosystems, simulate sustainability, and solve the challenge in under 8 minutes — or under 4 if you're Pro.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 mt-4 sm:mt-8">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={scrollToSolver}
                    className="group relative px-4 sm:px-8 py-2 sm:py-4 w-full sm:w-auto text-center bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    Launch Solver Now
                    <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.href = '/ecosystemguide'}
                    className="group relative px-4 sm:px-8 py-2 sm:py-4 w-full sm:w-auto text-center border-2 border-blue-600 text-blue-600 text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 bg-white/70 hover:bg-blue-50/80"
                  >
                    Ecosystem Guide
                    <div className="absolute inset-0 bg-blue-600 rounded-xl opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowVideoModal(true)}
                    className="group relative px-4 sm:px-8 py-2 sm:py-4 w-full sm:w-auto text-center bg-gradient-to-r from-gray-700 to-gray-600 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    Watch Tutorial
                    <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </motion.button>
                </div>
                <p className="text-xs sm:text-sm text-blue-100/80 mt-4 max-w-md">
                  Cognidoor does not keep or store McKinsey data - it is only processed and deleted immediately
                </p>
              </motion.div>
            </div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="absolute top-[120px] right-[-20px] md:right-[-10px] w-[400px] aspect-[2/3] z-10 hidden md:block hero-image"
            >
              <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img 
                  src={ecosystemImage} 
                  alt="Ecosystem Solver Preview" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          {/* Features Grid - Move to top */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8 mb-16">
            <motion.div
              whileHover={{ y: -4 }}
              className="feature-card p-4 sm:p-6 rounded-xl"
            >
              <div className="feature-icon">
                <BoltIcon className="h-5 sm:h-6 w-5 sm:w-6 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Lightning-Fast Results</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Get your solution in under 8 minutes. With Pro and screenshot upload, solve in ~3 minutes!
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="feature-card p-4 sm:p-6 rounded-xl"
            >
              <div className="feature-icon">
                <CheckCircleIcon className="h-5 sm:h-6 w-5 sm:w-6 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Accurate & Smart</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Powered by the same logic as the real game, ensuring your solution meets all criteria.
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="feature-card p-4 sm:p-6 rounded-xl"
            >
              <div className="feature-icon">
                <AcademicCapIcon className="h-5 sm:h-6 w-5 sm:w-6 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Learn as You Practice</h3>
              <p className="text-sm sm:text-base text-gray-600">
                See which species thrive together. Practice unlimited scenarios.
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="feature-card p-4 sm:p-6 rounded-xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-20 sm:w-28 h-20 sm:h-28 bg-blue-100 rounded-bl-full transform translate-x-10 sm:translate-x-14 -translate-y-10 sm:-translate-y-14"></div>
              <div className="absolute bottom-0 left-0 w-20 sm:w-28 h-20 sm:h-28 bg-blue-100 rounded-tr-full transform -translate-x-10 sm:-translate-x-14 translate-y-10 sm:translate-y-14"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 sm:gap-4 mb-3 sm:mb-5">
                  <span className="text-xl sm:text-2xl bg-blue-100 w-9 sm:w-11 h-9 sm:h-11 rounded-xl flex items-center justify-center shadow-inner">📊</span>
                  <div className="flex-1">
                    <h3 className="text-blue-600 font-semibold text-sm sm:text-base">View our ecosystem data</h3>
                    <p className="text-xs sm:text-sm text-blue-700 mt-1">Use our ecosystem cards to test the solver and practice the ecosystem game</p>
                    <div className="h-1 w-10 sm:w-12 bg-gradient-to-r from-blue-200 to-blue-100 rounded mt-1 sm:mt-2"></div>
                  </div>
                  <div className="relative">
                    <div className="absolute -top-6 sm:-top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <div className="h-0.5 w-4 sm:w-8 bg-gradient-to-r from-transparent to-blue-500"></div>
                        <span className="text-xs sm:text-sm text-blue-500 font-medium tracking-wider animate-pulse">PRESS ME</span>
                        <div className="h-0.5 w-4 sm:w-8 bg-gradient-to-l from-transparent to-blue-500"></div>
                      </div>
                    </div>
                    <motion.button 
                      whileHover={{ scale: 1.1, rotate: 180 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowEcosystemData(!showEcosystemData)}
                      className="w-8 sm:w-11 h-8 sm:h-11 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white shadow-lg transform transition-transform duration-200 group"
                    >
                      <span className="text-lg sm:text-xl transform group-hover:rotate-12 transition-transform duration-200">🔌</span>
                    </motion.button>
                  </div>
                </div>
                <AnimatePresence>
                  {showEcosystemData && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
                        {ecosystemDataSets.map((data, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-blue-50 rounded-lg p-2 sm:p-3 hover:bg-blue-100 transition-colors cursor-pointer group"
                            onClick={() => handleEcosystemClick(data)}
                          >
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="text-lg sm:text-xl group-hover:scale-110 transition-transform">{data.icon}</span>
                              <div className="flex-1">
                                <h4 className="font-medium text-blue-900 text-sm sm:text-base">{data.name}</h4>
                                <p className="text-xs sm:text-sm text-blue-700">{data.stats}</p>
                              </div>
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                →
                              </motion.div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          {/* Solver Interface - Move to bottom */}
          <motion.div
            ref={solverRef}
            className="solver-container solver-card p-6 sm:p-8 relative overflow-hidden bg-gradient-to-br from-gray-50/95 to-white/90"
            onClick={generateRandomSymbols}
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 sm:gap-4 mb-8 sm:mb-12">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 180 }}
                  whileTap={{ scale: 0.9 }}
                  className="rotate-button"
                >
                  🎮
                </motion.div>
                <div className="title-container">
                  <h2 className="premium-title text-xl sm:text-2xl md:text-3xl">Ecosystem Solver</h2>
                  <div className="title-underline"></div>
                  <p className="text-gray-600 mt-2 text-sm sm:text-base">Let Cognidoor crunch the numbers while you relax</p>
                </div>
              </div>

              {/* Math Symbols Animation Container */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {mathSymbols.map((item, index) => (
                  <motion.div
                    key={`${index}-${item.symbol}`}
                    className="absolute text-3xl text-blue-400/20"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: [item.opacity, item.opacity + 0.2],
                      y: [0, -15, 0],
                      x: [-10, 10, -10],
                      rotate: [-8, 8, -8],
                      scale: [item.scale, item.scale * 1.1, item.scale]
                    }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      repeatType: "reverse",
                      delay: item.delay,
                      ease: "easeInOut"
                    }}
                    style={{
                      top: `${item.top}%`,
                      left: `${item.left}%`,
                    }}
                  >
                    {item.symbol}
                  </motion.div>
                ))}
              </div>

              {/* Progress Steps */}
              {renderSolverSteps()}

              {/* Method Selection */}
              {activeStep === 1 && renderMethodSelection()}

              {/* Data Entry */}
              {activeStep === 2 && renderDataEntry()}

              {/* Results */}
              {activeStep === 3 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {loading ? (
                    renderSolvingAnimation()
                  ) : (
                    <Results
                      result={result}
                      producers={producersState}
                      animals={animalsState}
                      onBack={() => setActiveStep(2)}
                    />
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Options Modal */}
        <AnimatePresence>
          {showOptionsModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
              >
                <h3 className="text-xl font-semibold mb-4">What would you like to do with {selectedEcosystem?.name}?</h3>
                <div className="space-y-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDownload}
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
                  >
                    {isLoading ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        <span>📥</span>
                        Download Ecosystem Images
                      </>
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleViewImages}
                    disabled={isLoading}
                    className="w-full bg-blue-50 text-blue-600 py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors"
                  >
                    {isLoading ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        <span>👁️</span>
                        View Ecosystem Images
                      </>
                    )}
                  </motion.button>
                </div>
                <button
                  onClick={() => setShowOptionsModal(false)}
                  className="mt-4 text-gray-500 hover:text-gray-700 w-full text-center"
                >
                  Cancel
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Image Viewer Modal */}
        <AnimatePresence>
          {showImageViewer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">{selectedEcosystem?.name} Images</h3>
                  <button
                    onClick={() => setShowImageViewer(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {viewerImages.map((image, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="relative aspect-video rounded-lg overflow-hidden bg-gray-100"
                      >
                        <img
                          src={image}
                          alt={`${selectedEcosystem?.name} ${index + 1}`}
                          className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            // console.error(`Failed to load image ${index + 1}`);
                            e.target.style.display = 'none';
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showVideoModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="bg-white rounded-2xl p-4 max-w-4xl w-full shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Placeholder for video content */}
              <div className="text-center text-lg font-semibold">Video content will be added here.</div>
              <div className="mt-4 flex justify-end">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowVideoModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add QuickReference at the bottom */}
      <QuickReference producers={producersState} animals={animalsState} isMobile={isMobile} />
    </div>
  );
};

export default DemoEcosystemSolverPage;